import React, { useEffect, useState } from 'react'
import { Flex, Box, Card, Text } from '@chilipiper/design-system'
import { LocationType, PickedMeetingLocation } from '@chilipiper/api-type-def'
import { useTranslations } from '@chilipiper/locale'
import noop from 'lodash/noop'
import isEqual from 'lodash/isEqual'
import { getLocationsLabels } from '@chilipiper/meetings-components'
import { Location } from './location/Location'
import { useLabelTranslation } from './hooks'

interface Props {
  locations: LocationType[]
  onSelect?: (location: PickedMeetingLocation) => void
  pickedMeetingLocation?: PickedMeetingLocation
}

export const MeetingLocation = ({ locations, onSelect = noop, pickedMeetingLocation }: Props) => {
  const [meetingLocationText, requiredText] = useTranslations(['meetingLocation', 'required'])
  const labelTranslation = useLabelTranslation()
  const labels = getLocationsLabels(locations)
  const size = labels.length
  const [selectedLocation, setSelectedLocation] = useState<PickedMeetingLocation>()
  const [guestInput, setGuestInput] = useState('')

  useEffect(() => {
    let nextSelectedLocation = locations.find(l => l.isDefault)

    if (pickedMeetingLocation?.type === 'AskTheGuest' && pickedMeetingLocation?.text !== '') {
      nextSelectedLocation = locations.find(l => l.type === 'AskTheGuest')
      setGuestInput(pickedMeetingLocation.text)
    }

    if (pickedMeetingLocation?.type === 'CallTheGuest' && pickedMeetingLocation?.phone !== '') {
      nextSelectedLocation = locations.find(l => l.type === 'CallTheGuest')
      setGuestInput(pickedMeetingLocation.phone)
    }

    if (selectedLocation || !nextSelectedLocation) {
      return
    }

    selectLocation(nextSelectedLocation as PickedMeetingLocation)
  }, [selectedLocation, locations, pickedMeetingLocation])

  const selectLocation = (location: PickedMeetingLocation) => {
    setSelectedLocation(location)
    onSelect(location)
  }

  const updateGuestInput = (value: string) => {
    setGuestInput(value)

    if (selectedLocation?.type === 'AskTheGuest') {
      onSelect({
        ...selectedLocation,
        text: value,
      })
    }

    if (selectedLocation?.type === 'CallTheGuest') {
      onSelect({
        ...selectedLocation,
        phone: value,
      })
    }
  }

  return (
    <Box data-test-id='MeetingLocation-container'>
      <Flex mb={3} alignItems='center' justifyContent='space-between'>
        <Text fontWeight={600}>{meetingLocationText}</Text>
        {!selectedLocation ? (
          <Text textStyle='body-monoline' color='neutral-dark-48'>
            {requiredText}
          </Text>
        ) : null}
      </Flex>
      <Card
        bg='bg/list-item-unselected'
        w='100%'
        p={0}
        boxShadow='box-neutral-default-out'
        h='auto'
        overflow='hidden'
      >
        {labels.map(({ label, location }, index) => (
          <Location
            key={label.name}
            name={labelTranslation[label.name] ?? label.name}
            icon={label.icon}
            divider={index < size - 1}
            checked={isEqual(selectedLocation, location)}
            onClick={() => selectLocation(location as PickedMeetingLocation)}
            description={label.description ? labelTranslation[label.description] : undefined}
            input={
              label.input
                ? {
                    label: labelTranslation[label.input.label],
                    placeholder: labelTranslation[label.input.placeholder],
                    onChange: updateGuestInput,
                    value: guestInput,
                    type: label.input.type,
                  }
                : undefined
            }
          />
        ))}
      </Card>
    </Box>
  )
}
